import { action, computed, makeObservable, observable, runInAction } from "mobx"

import { Currency } from "@src/app/CurrenciesCollection"
import { catchErrors, convertsApi } from "@src/shared/api"
import {
  ConvertPairsResponse,
  ConvertPairsResponseFrom,
} from "@src/shared/api/generated"
import Collection from "@src/shared/classes/Collection/Collection"
import { loading } from "@src/shared/classes/utils/loading"
import { wrapSingleton } from "@src/shared/classes/utils/wrapSingleton"

export type ConvertPairsType = {
  from: ConvertPairsItemType
  to: ConvertPairsItemType
}

export type ConvertPairsItemType = Required<ConvertPairsResponseFrom> & {
  id: string
}
export class ConvertPairsCollection extends loading(
  ["load"],
  Collection<ConvertPairsType>,
) {
  private loadVersion = 0

  isLoaded = false

  constructor() {
    super()

    makeObservable(this, {
      from: computed,
      getFromPairsByTo: action.bound,
      getToPairsByFrom: action.bound,
      isLoaded: observable,
      load: action.bound,
      reset: action.bound,
      to: computed,
    })
  }

  #defaultSort(items: ConvertPairsItemType[]) {
    return items.slice().sort((a, b) => {
      if (b.network === "fiat") {
        return -1
      }

      return 0
    })
  }

  async _loadPairs(type?: "bank" | "card" | undefined) {
    const { data: { data } = {} } = await convertsApi.getConvertsPairs(type)

    return data
  }

  getFromPairsByTo(to: Currency): ConvertPairsItemType[] {
    return this.#defaultSort(
      this.items
        .filter(({ to: { id } }) => `${to.symbol}_${to.network}` === id)
        .map((item) => item.from),
    )
  }

  getToPairsByFrom(from: Currency): ConvertPairsItemType[] {
    return this.#defaultSort(
      this.items
        .filter(({ from: { id } }) => `${from.symbol}_${from.network}` === id)
        .map((item) => item.to),
    )
  }

  hasFromPair({ network, symbol }: { network: string; symbol: string }) {
    const id = `${symbol}_${network}`

    return this.items.some(({ from }) => from.id === id)
  }

  async load() {
    const loadVersion = ++this.loadVersion

    try {
      const data = (
        await Promise.allSettled([
          this._loadPairs("card"),
          this._loadPairs("bank"),
          this._loadPairs(),
        ])
      )
        .flat()
        .reduce((acc, data) => {
          if (data.status === "fulfilled") {
            acc.push(...(data.value as ConvertPairsResponse[]))
          }

          return acc
        }, [] as ConvertPairsResponse[])

      if (data && loadVersion === this.loadVersion) {
        this.replace(
          data.map((i): ConvertPairsType => {
            const { from, to } = i as Required<ConvertPairsResponse>

            return {
              from: {
                ...(from as Required<ConvertPairsResponseFrom>),
                id: `${from.symbol}_${from.network}`,
              },
              to: {
                ...(to as Required<ConvertPairsResponseFrom>),
                id: `${to.symbol}_${to.network}`,
              },
            }
          }),
        )
      }
    } catch (e: any) {
      catchErrors(e)
    } finally {
      runInAction(() => {
        if (loadVersion === this.loadVersion) {
          this.isLoaded = true
        }
      })
    }
  }

  reset() {
    this.loadVersion += 1
    this.isLoaded = false
    this.replace()
  }

  get from(): ConvertPairsItemType[] {
    return this.#defaultSort(
      this.items.reduce(
        (acc: ConvertPairsItemType[], current: ConvertPairsResponse) => {
          const from = current.from as ConvertPairsItemType

          const alreadyHas = acc.some(({ id }) => id === from.id)

          if (!alreadyHas) {
            acc.push(from)
          }

          return acc
        },
        [] as ConvertPairsItemType[],
      ),
    )
  }

  get to(): ConvertPairsItemType[] {
    return this.#defaultSort(
      this.items.reduce(
        (acc: ConvertPairsItemType[], current: ConvertPairsResponse) => {
          const to = current.to as ConvertPairsItemType

          const alreadyHas = acc.some(({ id }) => id === to.id)

          if (!alreadyHas) {
            acc.push(to)
          }

          return acc
        },
        [] as ConvertPairsItemType[],
      ),
    )
  }
}

export const GlobalConvertPairsCollection = wrapSingleton(
  ConvertPairsCollection,
)
